import type {
  Booking,
  CalendarResponse,
  DashboardOverview,
  DashboardOverviewQuery,
  DashboardSummary,
  EventSummary,
  MobileBootstrap,
  ProviderPortalMetrics,
  ReportBookingPoint,
  ReportOverview,
  ReportRevenuePoint
} from "@kuroo/contracts";

export type SdkAnalyticsContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function dashboardSummary(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<DashboardSummary> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<DashboardSummary>(`/api/v1/dashboard/summary${query.size ? `?${query}` : ""}`);
}

export function dashboardOverview(context: SdkAnalyticsContext, filters: DashboardOverviewQuery = {}): Promise<DashboardOverview> {
  const query = new URLSearchParams();
  if (filters.view) query.set("view", filters.view);
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  if (filters.month) query.set("month", filters.month);
  if (filters.serviceId) query.set("serviceId", filters.serviceId);
  if (filters.providerId) query.set("providerId", filters.providerId);
  if (filters.locationId) query.set("locationId", filters.locationId);
  if (filters.upcomingStatus) query.set("upcomingStatus", filters.upcomingStatus);
  return context.request<DashboardOverview>(`/api/v1/dashboard/overview${query.size ? `?${query}` : ""}`);
}

export function reportOverview(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<ReportOverview> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<ReportOverview>(`/api/v1/reports/overview${query.size ? `?${query}` : ""}`);
}

export function reportRevenue(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportRevenuePoint[] }> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<{ range: { from: string; to: string }; points: ReportRevenuePoint[] }>(`/api/v1/reports/revenue${query.size ? `?${query}` : ""}`);
}

export function reportBookings(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportBookingPoint[] }> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<{ range: { from: string; to: string }; points: ReportBookingPoint[] }>(`/api/v1/reports/bookings${query.size ? `?${query}` : ""}`);
}

export function providerPortalMetrics(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<ProviderPortalMetrics> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<ProviderPortalMetrics>(`/api/v1/reports/provider-portal${query.size ? `?${query}` : ""}`);
}

export function mobileBootstrap(context: SdkAnalyticsContext): Promise<MobileBootstrap> {
  return context.request<MobileBootstrap>("/api/v1/mobile/bootstrap");
}

export function mobileAppointments(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; appointments: Booking[] }> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<{ range: { from: string; to: string }; appointments: Booking[] }>(`/api/v1/mobile/appointments${query.size ? `?${query}` : ""}`);
}

export function mobileEvents(context: SdkAnalyticsContext, range: { from?: string; to?: string } = {}): Promise<EventSummary[]> {
  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  return context.request<EventSummary[]>(`/api/v1/mobile/events${query.size ? `?${query}` : ""}`);
}

export function calendar(context: SdkAnalyticsContext, filters: { from?: string; to?: string; providerId?: string; locationId?: string; serviceId?: string; status?: string; includeEvents?: boolean; includeSlots?: boolean } = {}): Promise<CalendarResponse> {
  const query = new URLSearchParams();
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  if (filters.providerId) query.set("providerId", filters.providerId);
  if (filters.locationId) query.set("locationId", filters.locationId);
  if (filters.serviceId) query.set("serviceId", filters.serviceId);
  if (filters.status) query.set("status", filters.status);
  if (filters.includeEvents) query.set("includeEvents", "true");
  if (filters.includeSlots === false) query.set("includeSlots", "false");
  return context.request<CalendarResponse>(`/api/v1/calendar${query.size ? `?${query}` : ""}`);
}
